// Saldo per plats (admin mockup §7): what is where, from the container projection.

import { useEffect, useMemo, useState } from "react";
import { Admin, type StockRow } from "../api/client.js";
import { fmtNum, fmtQty, t } from "../i18n.js";

export function Stock() {
  const [rows, setRows] = useState<StockRow[] | null>(null);
  const [mode, setMode] = useState<"location" | "product">("location");
  const [q, setQ] = useState("");
  useEffect(() => {
    Admin.stock().then(setRows).catch(() => setRows([]));
  }, []);

  // Gone goods (shipped, adjusted out) are history, not stock (owner 2026-11-02).
  const live = useMemo(() => (rows ?? []).filter((r) => r.state !== "shipped" && r.state !== "adjusted-out" && r.state !== "emptied" && r.state !== "merged"), [rows]);
  const roots = useMemo(() => live.filter((r) => r.parent_id === null), [live]);
  // Children looked up once, not by filtering every row per pallet (7,000 loose rows froze the page).
  const kidsOf = useMemo(() => {
    const m = new Map<string, StockRow[]>();
    for (const r of live) if (r.parent_id) (m.get(r.parent_id) ?? m.set(r.parent_id, []).get(r.parent_id)!).push(r);
    return m;
  }, [live]);
  // Rendered in pages: a mirrored warehouse is thousands of rows; search narrows, "Visa fler" extends.
  const PAGE = 300;
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => setLimit(PAGE), [q, mode]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = roots.filter((r) => !needle || [r.location_code, r.product_name, r.sku, r.lot_number, r.sscc, r.gtin].some((v) => v?.toLowerCase().includes(needle)));
    return base;
  }, [roots, q]);

  const byProduct = useMemo(() => {
    const m = new Map<string, { name: string; sku: string | null; units: number; sums: Map<string, number>; locations: Set<string> }>();
    for (const r of live) {
      if (r.kind === "logistic-unit") continue;
      const key = r.sku ?? r.gtin ?? "?";
      const e = m.get(key) ?? { name: r.product_name ?? `GTIN ${r.gtin ?? "?"}`, sku: r.sku, units: 0, sums: new Map(), locations: new Set() };
      e.units += 1;
      e.sums.set(r.uom, (e.sums.get(r.uom) ?? 0) + Number(r.quantity));
      if (r.location_code) e.locations.add(r.location_code);
      m.set(key, e);
    }
    return [...m.values()].filter((e) => !q.trim() || e.name.toLowerCase().includes(q.trim().toLowerCase()) || e.sku?.toLowerCase().includes(q.trim().toLowerCase()));
  }, [live, q]);

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.st.title")}</h1><div className="crumb">{t("adm.st.sub")} · {t("adm.st.rows", { n: mode === "location" ? filtered.length : byProduct.length })}</div></div>
        <div className="right">
          <input className="ain" style={{ height: 36, width: 280, borderRadius: 10, border: "1px solid var(--line)", background: "var(--bg)", padding: "0 12px", font: "inherit", color: "var(--text)" }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.st.search")} />
          <span className="seg"><button type="button" className={mode === "location" ? "on" : ""} onClick={() => setMode("location")}>{t("adm.st.byLocation")}</button><button type="button" className={mode === "product" ? "on" : ""} onClick={() => setMode("product")}>{t("adm.st.byProduct")}</button></span>
        </div>
      </div>
      <div className="adm-content">
        <div className="card">
          <div className="bd flush">
            {mode === "location" ? (
              <table className="at">
                <thead><tr><th>{t("adm.st.location")}</th><th>{t("adm.st.unit")}</th><th>{t("adm.st.product")}</th><th>{t("adm.st.lot")}</th><th className="num">{t("adm.st.count")}</th><th className="num">{t("adm.st.content")}</th><th>{t("adm.st.bestBefore")}</th><th>{t("adm.col.status")}</th></tr></thead>
                <tbody>
                  {filtered.slice(0, limit).map((r) => {
                    const kids = kidsOf.get(r.container_id) ?? [];
                    const sums = new Map<string, number>();
                    for (const k of kids.length ? kids : [r]) sums.set(k.uom, (sums.get(k.uom) ?? 0) + Number(k.quantity));
                    const products = new Set(kids.map((k) => k.product_name ?? k.gtin ?? "?"));
                    const bb = (kids.length ? kids : [r]).map((k) => k.best_before).filter(Boolean).sort()[0] ?? null;
                    const unplaced = !r.location_code || r.state === "received";
                    return (
                      <tr key={r.container_id}>
                        <td className="id">{r.location_code ?? "—"}</td>
                        <td className="id">{r.kind === "logistic-unit" ? `${t("unit.pallet")} ${r.sscc ? `${r.sscc.slice(0, 4)}…${r.sscc.slice(-4)}` : ""}` : r.kind === "loose" ? t("unit.loose") : t("unit.kolli")}</td>
                        <td>{kids.length ? (products.size === 1 ? [...products][0] : `${products.size} ${t("adm.products").toLowerCase()}`) : r.product_name ?? `GTIN ${r.gtin ?? "?"}`}</td>
                        <td className="id mut">{kids.length ? `${new Set(kids.map((k) => k.lot_number)).size} ${t("adm.st.lot").toLowerCase()}` : r.lot_number ?? "—"}</td>
                        <td className="num">{kids.length || 1}</td>
                        <td className="num">{[...sums].map(([uom, v]) => fmtQty(v, uom)).join(" + ")}</td>
                        <td className="id">{bb ?? "—"}</td>
                        <td>{unplaced ? <span className="pill warn">{t("adm.st.unplaced")}</span> : <span className="pill ok">{t("adm.st.stored")}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <table className="at">
                <thead><tr><th>{t("adm.st.product")}</th><th className="num">{t("adm.st.count")}</th><th className="num">{t("adm.st.content")}</th><th>{t("adm.st.location")}</th></tr></thead>
                <tbody>
                  {byProduct.map((e) => <tr key={e.sku ?? e.name}><td>{e.name}{e.sku ? <span className="mut"> {e.sku}</span> : null}</td><td className="num">{fmtNum(e.units)}</td><td className="num">{[...e.sums].map(([uom, v]) => fmtQty(v, uom)).join(" + ")}</td><td className="id">{[...e.locations].join(", ") || "—"}</td></tr>)}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
